// API service for making requests to the backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Cache for storing API responses
const apiCache = {
  formOptions: null,
  formOptionsExpiry: null,
  platforms: null,
  platformsExpiry: null,
  contentTypes: null,
  contentTypesExpiry: null
};

// Cache expiration time in milliseconds (5 minutes)
const CACHE_EXPIRATION = 5 * 60 * 1000;

// Default API object for components that import the default export
const api = {
  // Social Media Content Generator
  
  // Get both platforms and content types in a single request with caching
  getFormOptions: async () => {
    try {
      // Check if we have a valid cache
      const now = Date.now();
      if (apiCache.formOptions && apiCache.formOptionsExpiry > now) {
        console.log('Using cached form options');
        return apiCache.formOptions;
      }
      
      const response = await fetch(`${API_URL}/social-media-content/form-options`);
      if (!response.ok) {
        throw new Error(`Failed to fetch form options: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache the response
      apiCache.formOptions = data;
      apiCache.formOptionsExpiry = now + CACHE_EXPIRATION;
      
      return data;
    } catch (error) {
      console.error('Error fetching form options:', error);
      throw error;
    }
  },
  
  // Legacy methods with caching
  getSocialMediaPlatforms: async () => {
    try {
      // Check if we have a valid cache
      const now = Date.now();
      if (apiCache.platforms && apiCache.platformsExpiry > now) {
        console.log('Using cached platforms');
        return apiCache.platforms;
      }
      
      // Try to get from form options cache first
      if (apiCache.formOptions && apiCache.formOptionsExpiry > now) {
        console.log('Using platforms from cached form options');
        return { platforms: apiCache.formOptions.platforms };
      }
      
      const response = await fetch(`${API_URL}/social-media-content/platforms`);
      if (!response.ok) {
        throw new Error(`Failed to fetch platforms: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache the response
      apiCache.platforms = data;
      apiCache.platformsExpiry = now + CACHE_EXPIRATION;
      
      return data;
    } catch (error) {
      console.error('Error fetching social media platforms:', error);
      throw error;
    }
  },
  
  getContentTypes: async () => {
    try {
      // Check if we have a valid cache
      const now = Date.now();
      if (apiCache.contentTypes && apiCache.contentTypesExpiry > now) {
        console.log('Using cached content types');
        return apiCache.contentTypes;
      }
      
      // Try to get from form options cache first
      if (apiCache.formOptions && apiCache.formOptionsExpiry > now) {
        console.log('Using content types from cached form options');
        return { contentTypes: apiCache.formOptions.contentTypes };
      }
      
      const response = await fetch(`${API_URL}/social-media-content/content-types`);
      if (!response.ok) {
        throw new Error(`Failed to fetch content types: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache the response
      apiCache.contentTypes = data;
      apiCache.contentTypesExpiry = now + CACHE_EXPIRATION;
      
      return data;
    } catch (error) {
      console.error('Error fetching content types:', error);
      throw error;
    }
  },
  
  generateContent: async (data) => {
    try {
      const response = await fetch(`${API_URL}/social-media-content/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate content');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error generating content:', error);
      throw error;
    }
  },
  
  // Digital Identity Hub
  getIdentityTemplates: async () => {
    try {
      const response = await fetch(`${API_URL}/digital-identity/templates`);
      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching identity templates:', error);
      throw error;
    }
  },
  
  createIdentity: async (data) => {
    try {
      const response = await fetch(`${API_URL}/digital-identity/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create identity');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating identity:', error);
      throw error;
    }
  },
  
  getIdentity: async (id) => {
    try {
      const response = await fetch(`${API_URL}/digital-identity/${id}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch identity: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching identity:', error);
      throw error;
    }
  },
  
  updateIdentity: async (id, data) => {
    try {
      const response = await fetch(`${API_URL}/digital-identity/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update identity');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating identity:', error);
      throw error;
    }
  },
  
  deleteIdentity: async (id) => {
    try {
      const response = await fetch(`${API_URL}/digital-identity/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete identity');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting identity:', error);
      throw error;
    }
  },
  
  getUserIdentities: async () => {
    try {
      const response = await fetch(`${API_URL}/digital-identity/user/identities`);
      if (!response.ok) {
        throw new Error(`Failed to fetch user identities: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching user identities:', error);
      throw error;
    }
  },
  
  generateQRCode: async (id) => {
    try {
      const response = await fetch(`${API_URL}/digital-identity/${id}/qrcode`);
      if (!response.ok) {
        throw new Error(`Failed to generate QR code: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error generating QR code:', error);
      throw error;
    }
  },
};

// Export the API object as default
export default api;

/**
 * Extract YouTube video ID from different URL formats
 * @param {string} url - YouTube URL
 * @returns {string|null} - Video ID or null if invalid
 */
export const extractVideoId = (url) => {
  if (!url) return null;
  
  // Regular expressions for different YouTube URL formats
  const regexPatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/user\/\S+\/\S+\/|youtube\.com\/user\/\S+\/u\/\d+\/|youtube\.com\/\S+\/\S+\/\S+\/|youtube\.com\/\S+\/u\/\d+\/|youtube\.com\/\S+\/u\/\S+\/|youtube\.com\/attribution_link\?a=\S+&u=\/watch\?v=)([^&#\/\s]+)/,
    /(?:youtube\.com\/shorts\/)([^&#\/\s]+)/,
    /(?:youtu\.be\/)([^&#\/\s]+)/
  ];
  
  for (const pattern of regexPatterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
};

/**
 * Get video information from YouTube URL
 * @param {string} url - YouTube URL
 * @returns {Promise<Object>} - Video information
 */
export const getVideoInfo = async (url) => {
  try {
    // Extract video ID for fallback
    const videoId = extractVideoId(url);
    
    if (!videoId) {
      throw new Error('Could not extract video ID from URL');
    }
    
    console.log('Fetching video info for:', url);
    
    try {
      // Make API call to backend
      const response = await fetch(`${API_URL}/youtube-reel/video-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });
      
      // If response is not OK, handle it gracefully
      if (!response.ok) {
        console.warn(`API returned status: ${response.status}`);
        // Throw to move to fallback
        throw new Error(`Status code: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Video info received:', data);
      
      // If the backend didn't return a videoId, add it
      if (!data.videoId) {
        data.videoId = videoId;
      }
      
      return data;
    } catch (apiError) {
      console.error('API error:', apiError);
      
      // Graceful fallback with basic information
      // This allows the embed to still work
      return {
        videoId,
        title: "YouTube Video",
        duration: 0, // Will be updated by the player
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
        fallback: true // Flag to indicate this is fallback data
      };
    }
  } catch (error) {
    console.error('Error fetching video info:', error);
    throw error;
  }
};

/**
 * Process video segments into a reel with progress tracking
 * @param {Object} data - Processing parameters
 * @param {string} data.url - YouTube URL
 * @param {Array} data.segments - Time segments
 * @param {string} data.aspectRatio - Target aspect ratio
 * @param {string} data.transitionType - Transition effect type
 * @param {string} data.quality - Output quality
 * @param {Function} onProgress - Progress callback (progress, stage)
 * @returns {Promise<Object>} - Processing result
 */
export const processVideo = async (data, onProgress) => {
  try {
    // First, start the processing job to get the jobId
    const response = await fetch(`${API_URL}/youtube-reel/process-video`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to process video');
    }
    
    const startResult = await response.json();
    
    if (!startResult.success) {
      throw new Error(startResult.error || 'Failed to start processing');
    }
    
    // Now set up SSE connection to track progress
    return new Promise((resolve, reject) => {
      const jobId = startResult.jobId;
      const eventSource = new EventSource(`${API_URL}/youtube-reel/process-video-status?jobId=${jobId}`);
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Call progress callback if provided
          if (onProgress && typeof onProgress === 'function') {
            onProgress(data.progress, data.stage, data.error);
          }
          
          // Check if processing is complete
          if (data.progress === 100 && data.stage === 'completed') {
            // Ensure the downloadUrl is complete with the base API URL
            let downloadUrl = data.downloadUrl;
            if (downloadUrl && !downloadUrl.startsWith('http')) {
              // Convert relative URL to absolute URL
              const baseUrl = API_URL.split('/api')[0]; // Get base URL without '/api'
              downloadUrl = `${baseUrl}${downloadUrl}`;
            }
            
            // Close the event source
            eventSource.close();
            
            // Resolve with result
            resolve({
              success: true,
              downloadUrl: downloadUrl
            });
          }
          
          // Check for errors
          if (data.progress === -1 && data.stage === 'error') {
            eventSource.close();
            reject(new Error(data.error || 'Error processing video'));
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };
      
      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        eventSource.close();
        
        // In production, we want to show a helpful error message
        if (process.env.NODE_ENV === 'production') {
          reject(new Error('Connection to server lost while processing video. This could be due to network issues or a server timeout. Please try again with a shorter video or fewer segments.'));
        } else {
          reject(new Error('Lost connection to server while processing video'));
        }
      };
    });
    
  } catch (error) {
    console.error('Error processing video:', error);
    
    // For development, provide a fallback with progress simulation
    if (process.env.NODE_ENV === 'development') {
      return new Promise((resolve) => {
        let progress = 0;
        const stages = [
          'downloading',
          'processing_segments',
          'combining_segments',
          'converting_aspect_ratio',
          'finalizing',
          'completed'
        ];
        
        // Calculate total duration based on segments
        const totalDuration = (data.segments.length * 500) + (data.quality === 'high' ? 2000 : 1000);
        const interval = Math.floor(totalDuration / 20); // 20 progress updates
        
        // Simulate progress updates
        const timer = setInterval(() => {
          progress += 5;
          const stageIndex = Math.min(Math.floor(progress / 20), stages.length - 1);
          
          if (onProgress && typeof onProgress === 'function') {
            onProgress(progress, stages[stageIndex], null);
          }
          
          if (progress >= 100) {
            clearInterval(timer);
            resolve({
              success: true,
              downloadUrl: 'data:video/mp4;base64,AAAA', // Just a tiny dummy data URL
              message: 'In development mode, download is simulated. In production, this would be a real video file.'
            });
          }
        }, interval);
      });
    }
    
    throw error;
  }
};
